import type { VariableExpenseGridRow } from '@clockwise/clients/customer-variable-expense-occurrences';
import type { Customer } from '@clockwise/clients/customers';
import type { ExpenseCatalog } from '@clockwise/clients/expense-catalogs';
import { ALL_DATE_RANGE_PRESETS, ApiError, type DateRangeValue } from '@clockwise/shared';
import { DATE_RANGE_PRESETS, DatePicker, DateRangePopover } from '@clockwise/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronLeft, Download, Loader2, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { CustomerAutocomplete } from '@/components/custom/customer-autocomplete';
import { ExpenseCatalogAutocomplete } from '@/components/custom/expense-catalog-autocomplete';
import { formatMoney } from '@/components/custom/invoice-money';
import { NoAccess } from '@/components/custom/no-access';
import { RequirePermission } from '@/components/custom/require-permission';
import { VariableExpenseImportSheet } from '@/components/custom/variable-expense-import-sheet';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { variableExpenseOccurrencesApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { lastSearch } from '@/lib/last-search';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import {
  describePeriod,
  GRID_PAGE_SIZE,
  type GridPeriod,
  rangeToPeriodOrNull,
  searchToRange,
  useVariableExpenseGrid,
} from '@/lib/variable-expense-hooks';

const searchSchema = z.object({
  /** The window, as a PRESET plus explicit bounds for `custom`. The preset is
   *  what is stored, never the dates it resolves to, so a bookmarked
   *  "previous month" still means the month before *today* when it is reopened
   *  next month. */
  preset: z.enum(ALL_DATE_RANGE_PRESETS).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  /** A whole month, as its first day. Superseded by `preset`/`from`/`to` but
   *  still read: the generate dialog links here with the month it is about to
   *  invoice, and those links are in the wild. `searchToRange` expands it. */
  month: z.string().optional(),
  /** Narrow to one customer — used by the deep link from a customer's page,
   *  and by the Customer filter. */
  customerId: z.string().uuid().optional(),
  /** Narrow to one catalog item. */
  expenseCatalogId: z.string().uuid().optional(),
  /** Which lines to show. Absent means Needs data: the page exists to clear a
   *  backlog, so it opens on the work rather than on the archive. */
  state: z.enum(['all', 'needs']).optional(),
  /** Zero-based page, in the URL so a colleague can be sent page 3. */
  page: z.coerce.number().int().min(0).optional(),
  /** Arrived from the generate sheet: offer a way back to it, carrying the
   *  selections it had. Without this the user lands here and the only route
   *  back re-opens generate with everything reset. */
  returnGenerate: z.boolean().optional(),
  gFreqs: z.string().optional(),
  gKind: z.string().optional(),
  /** Open the import sheet on arrival. Set by the generate dialog's "Import a
   *  sheet" link, so that button lands on the upload rather than on a page
   *  where the user has to find it again. */
  import: z.boolean().optional(),
});

/**
 * The month grid: every variable expense line in force for a month, and what
 * it cost.
 *
 * A CHILD of `/invoices`, deliberately, so it inherits that sidebar entry
 * rather than adding one — the same arrangement as `/invoices/runs` and
 * `/invoices/settings`. It is reached from the invoice list's overflow menu.
 *
 * That is currently the ONLY door. An earlier version also put a warning panel
 * on the invoice list whenever a month had unfilled lines, which is what made
 * the page discoverable without a nav entry; it was removed as too heavy above
 * a 500-row table. The gap it leaves is real — nothing now tells someone the
 * month is incomplete before they generate — and the draft invoice's
 * `MISSING_VARIABLE_EXPENSE` flag is the only remaining signal, which arrives
 * one step too late to prevent the short invoice.
 *
 * The unit of the screen is the LINE — one row per thing that needs an amount
 * — with its recorded occurrences underneath. A line with none is the state
 * this page exists to surface, so it is highlighted rather than merely blank:
 * an unfilled line is not billed at all.
 */
export const Route = createFileRoute('/_authenticated/invoices/variable-expenses')({
  validateSearch: searchSchema,
  component: VariableExpensesPage,
});

function VariableExpensesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const range = searchToRange(search);
  // `null` while a Custom range is half-picked. Everything downstream reads it,
  // so an unfinished window disables the query rather than firing one against
  // a period the user has not finished naming.
  const period = rangeToPeriodOrNull(range);
  const state = search.state ?? 'needs';
  const page = search.page ?? 0;
  const canEdit = useHasPermission('customer.expense.edit');
  // The `/invoices` layout only gates `invoice.view`. This page reads customer
  // expense data, which is a separate grant — so it needs its own guard, or
  // every query on it would 403 into an error banner.
  const canView = useHasPermission('customer.expense.view');

  const [importOpen, setImportOpen] = useState(search.import === true);
  // One buffer for the whole page — see `useAmountDrafts`.
  const drafts = useAmountDrafts(period);
  // `null` month disables the query. The NoAccess return below is AFTER the
  // hooks (they cannot be called conditionally), so without this the page
  // would still fire a request it knows will 403.
  const { data, isPending, isFetching, isError, error } = useVariableExpenseGrid(
    canView ? period : null,
    {
      ...(search.customerId ? { customerId: search.customerId } : {}),
      ...(search.expenseCatalogId ? { expenseCatalogId: search.expenseCatalogId } : {}),
      state,
      offset: page * GRID_PAGE_SIZE,
    },
  );

  /** Writing the window back to the URL. The preset is stored; `from`/`to`
   *  ride along only for Custom, and `month` is dropped because an explicit
   *  pick has just superseded whatever the deep link asked for. */
  const setRange = (next: DateRangeValue) =>
    navigate({
      search: (sp) => ({
        ...sp,
        preset: next.preset,
        from: next.preset === 'custom' ? next.from : undefined,
        to: next.preset === 'custom' ? next.to : undefined,
        month: undefined,
        page: 0,
      }),
    });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  // The whole period's outstanding count, not this page's — it is the badge,
  // and a badge that changed when you turned the page would be describing the
  // page rather than the work.
  const needsCount = data?.meta.needsCount ?? 0;
  // Refetching with rows already on screen. `isPending` covers the first load
  // (skeletons); this is the narrower "what you are looking at is one question
  // out of date" case that `keepPreviousData` creates.
  const busy = isFetching && !isPending;
  // The heading, the two empty states and the download filename all say the
  // same thing about the window, from one place.
  const periodLabel = period ? describePeriod(period) : 'the selected range';

  /**
   * The month to hand back to the generate sheet.
   *
   * `search.month` is what the sheet sent, but picking a period here clears it
   * (`setRange`, deliberately). Falling back to the current period's start —
   * only when it IS a first-of-month, since the sheet's dropdown has no option
   * for a mid-month date — keeps the return trip on the period the user was
   * actually looking at rather than silently reverting to the default month.
   */
  const backMonth =
    search.month ?? (period?.periodStart.endsWith('-01') ? period.periodStart : undefined);
  const downloadQuery = period
    ? {
        ...period,
        ...(search.customerId ? { customerId: search.customerId } : {}),
        ...(search.expenseCatalogId ? { expenseCatalogId: search.expenseCatalogId } : {}),
      }
    : null;

  const download = async () => {
    if (!downloadQuery) return;
    try {
      const { blob, filename } = await variableExpenseOccurrencesApi.exportXlsx(downloadQuery);
      triggerDownload(blob, filename ?? `variable-expenses-${downloadQuery.periodStart}.xlsx`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Download failed');
    }
  };

  if (!canView) {
    return <NoAccess permission="customer.expense.view" resource="Variable expenses" />;
  }

  return (
    <PageShell>
      {/* A breadcrumb, not a button. "Back to invoices" sat in the action row
          reading as a call to action — the one thing a way OUT must never
          look like. */}
      {search.returnGenerate ? (
        <Link
          className="-mb-2 inline-flex w-fit items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
          search={{
            generate: true,
            ...(backMonth ? { gMonth: backMonth } : {}),
            ...(search.gFreqs ? { gFreqs: search.gFreqs } : {}),
            ...(search.gKind ? { gKind: search.gKind } : {}),
          }}
          to="/invoices"
        >
          <ChevronLeft className="size-4" />
          Back to generating invoices
        </Link>
      ) : (
        <Link
          className="-mb-2 inline-flex w-fit items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
          to="/invoices"
          search={lastSearch('/invoices')}
        >
          <ChevronLeft className="size-4" />
          Invoices
        </Link>
      )}

      <PageHeader
        title={`Variable expenses · ${periodLabel}`}
        subtitle="What each variable expense cost this period. Lines with no amount are left off the invoice."
      >
        <Button disabled={!downloadQuery} onClick={download} variant="outline">
          <Download className="size-4" />
          Export
        </Button>
        {/* No separate "Template" button. The export already carries a blank
            row for every line with no amount, so it IS the fill-in sheet — and
            a template that blanked a line's EXISTING amounts would invite
            someone to add a duplicate fee rather than correct one. */}
        {/* Primary, because it is the main road: anyone holding these figures
            has them in a spreadsheet, and typing is the fallback for the last
            few. */}
        {canEdit ? (
          <Button onClick={() => setImportOpen(true)}>
            <Upload className="size-4" />
            Import amounts
          </Button>
        ) : null}
      </PageHeader>

      {/* Period → show → customer → expense, widest scope first. All of them
          narrow the LINES, so the needs count narrows with them — the count and
          the table always answer the same question. */}
      <div className="flex flex-wrap items-center gap-2">
        <DateRangePopover onChange={setRange} presets={DATE_RANGE_PRESETS} value={range} />
        {/* A filter, not tabs. Once the empty row became editable in place,
            "Needs data" was the same table as "All" narrowed to lines with no
            figure — two tabs over one table was a filter in disguise.

            The trigger is drawn by hand rather than echoing the selected item,
            because the count has to survive switching to All lines: it is the
            page's most useful signal, and a filter that hid it the moment you
            looked at everything would make the backlog invisible. */}
        <Select
          onValueChange={(v) =>
            navigate({ search: (sp) => ({ ...sp, state: v as 'needs' | 'all', page: 0 }) })
          }
          value={state}
        >
          <SelectTrigger className="h-8 w-auto gap-2">
            <span>{state === 'needs' ? 'Needs data' : 'All lines'}</span>
            {/* Only when there IS a backlog. A "0" on the thing you are
                trying to empty reads as a score you cannot improve. */}
            {needsCount > 0 ? (
              state === 'needs' ? (
                <span className="rounded-full bg-amber-100 px-1.5 text-amber-800 text-xs tabular-nums dark:bg-amber-950 dark:text-amber-400">
                  {needsCount}
                </span>
              ) : (
                <span className="text-muted-foreground text-xs tabular-nums">
                  · {needsCount} need data
                </span>
              )
            ) : null}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="needs">Needs data</SelectItem>
            <SelectItem value="all">All lines</SelectItem>
          </SelectContent>
        </Select>
        {/* Width on a WRAPPER, not on the picker: Combobox's own container is
            `w-full` and its `className` lands on the inner input, so sizing it
            through the prop leaves a full-width control with a short box in it. */}
        <div className="w-64">
          <CustomerAutocomplete
            onChange={(c: Customer | null) =>
              navigate({ search: (sp) => ({ ...sp, customerId: c?.id, page: 0 }) })
            }
            placeholder="All customers"
            value={search.customerId ?? null}
          />
        </div>
        <div className="w-64">
          <ExpenseCatalogAutocomplete
            onChange={(e: ExpenseCatalog | null) =>
              navigate({ search: (sp) => ({ ...sp, expenseCatalogId: e?.id, page: 0 }) })
            }
            placeholder="All expense items"
            value={search.expenseCatalogId ?? null}
          />
        </div>
        {search.customerId || search.expenseCatalogId ? (
          <Button
            onClick={() =>
              navigate({
                search: (sp) => ({
                  ...sp,
                  customerId: undefined,
                  expenseCatalogId: undefined,
                  page: 0,
                }),
              })
            }
            size="sm"
            variant="ghost"
          >
            Clear
          </Button>
        ) : null}
        {busy ? (
          <Loader2 aria-label="Loading" className="size-4 animate-spin text-muted-foreground" />
        ) : null}
      </div>

      {/* Stale-while-revalidating made visible. `keepPreviousData` deliberately
          keeps the previous rows up while a new window or filter loads, and
          until now nothing said so — the table just sat there answering the
          question before last. Dimmed and inert rather than swapped for
          skeletons: those rows are still the last true answer, and discarding
          them is exactly what `keepPreviousData` exists to avoid. The spinner
          in the filter row carries the positive signal; dimming alone reads as
          "disabled", not "working". */}
      <div
        aria-busy={busy}
        className={busy ? 'contents [&>*]:pointer-events-none [&>*]:opacity-50' : 'contents'}
      >
        {period === null ? (
          // A Custom range with one bound (what the calendar emits on the first
          // click of a range), or a preset with no window. The query is off, so
          // without this the skeleton below would wait forever on nothing.
          <div data-surface="panel" className="rounded-lg border p-6 text-center light:bg-card">
            <p className="font-medium text-sm">Choose a date range</p>
            <p className="mt-1 text-muted-foreground text-sm">
              Pick both a start and an end date to see the variable expenses in it.
            </p>
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-destructive/50 p-4" role="alert">
            <p className="text-destructive text-sm">
              {error instanceof Error ? error.message : "Couldn't load variable expenses."}
            </p>
          </div>
        ) : isPending ? (
          // Skeleton rows, not a "Loading…" line: the table's shape is the thing
          // this page is about, and the rest of the app's list tables (DataTable)
          // hold their geometry the same way while the first page loads.
          <GridSkeleton columns={5} />
        ) : rows.length === 0 ? (
          /* Two different empty states, because they mean opposite things. On
           Needs data an empty table is the WIN condition and should say so;
           on All it means nothing is set up at all. */
          <div data-surface="panel" className="rounded-lg border p-6 text-center light:bg-card">
            {state === 'needs' ? (
              <>
                <p className="font-medium text-sm">Nothing needs an amount for {periodLabel}</p>
                <p className="mt-1 text-muted-foreground text-sm">
                  Every variable expense in force this period has a figure. The invoices will bill
                  them in full.
                </p>
              </>
            ) : (
              <>
                <p className="font-medium text-sm">Nothing to record for {periodLabel}</p>
                <p className="mt-1 text-muted-foreground text-sm">
                  No customer has a variable expense in force for this period. Variable expenses are
                  set up on a customer's Invoicing tab, from a catalog item marked variable.
                </p>
              </>
            )}
          </div>
        ) : (
          <GridCard>
            <Table>
              <TableHeader>
                <TableRow>
                  {/* The last column has to hold "Add another" beside a Cancel,
                      which is the widest thing in the table — at a narrower share
                      the buttons clipped against the table's edge. */}
                  <TableHead className="w-[22%]">Customer</TableHead>
                  <TableHead className="w-[24%]">Expense</TableHead>
                  <TableHead className="w-[16%]">Date</TableHead>
                  <TableHead className="w-[16%] text-right">Amount</TableHead>
                  <TableHead className="w-[22%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <GridRows canEdit={canEdit} drafts={drafts} key={row.lineId} row={row} />
                ))}
              </TableBody>
            </Table>
          </GridCard>
        )}
      </div>

      <SaveBar drafts={drafts} />

      {total > GRID_PAGE_SIZE ? (
        <div className="flex items-center justify-between text-muted-foreground text-sm">
          <span>
            Showing {page * GRID_PAGE_SIZE + 1}–{Math.min((page + 1) * GRID_PAGE_SIZE, total)} of{' '}
            {total}
          </span>
          <div className="flex gap-2">
            <Button
              disabled={page === 0}
              onClick={() => navigate({ search: (s) => ({ ...s, page: page - 1 }) })}
              size="sm"
              variant="outline"
            >
              Previous
            </Button>
            <Button
              disabled={(page + 1) * GRID_PAGE_SIZE >= total}
              onClick={() => navigate({ search: (s) => ({ ...s, page: page + 1 }) })}
              size="sm"
              variant="outline"
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <RequirePermission permission="customer.expense.edit">
        {downloadQuery ? (
          <VariableExpenseImportSheet
            onOpenChange={setImportOpen}
            open={importOpen}
            query={downloadQuery}
          />
        ) : null}
      </RequirePermission>
    </PageShell>
  );
}

/**
 * The white table card every other list page sits its table in (`DataTable`'s
 * `StickyTableScroll`, the rate card lines): `data-surface="table"` for the
 * shared taupe border and card radius, and the header row on the
 * `bg-surface-subtle` band so it reads apart from the white body rows.
 */
function GridCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-surface="table"
      className="overflow-hidden rounded-md border light:bg-card [&_thead_th]:bg-surface-subtle"
    >
      {children}
    </div>
  );
}

/**
 * The table's shape, while the first page is on its way.
 *
 * Skeleton rows rather than a "Loading…" line, matching `DataTable` and every
 * other list in the app: the geometry is what the reader is about to use, so
 * holding it stops the page jumping under them when the rows land.
 */
function GridSkeleton({ columns }: { columns: number }) {
  return (
    <GridCard>
      <Table>
        <TableBody>
          {Array.from({ length: 6 }).map((_, r) => (
            <TableRow key={`skeleton-${r}`}>
              {Array.from({ length: columns }).map((_c, i) => (
                <TableCell key={`skeleton-${r}-${i}`}>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </GridCard>
  );
}

/**
 * One amount typed but not yet written, per line.
 *
 * `incurredOn` rides along because a fee has a real date and a window can be
 * longer than a day. It defaults to the window's start — the only defensible
 * choice when nobody has said otherwise — and the row's date picker changes it.
 */
interface AmountDraft {
  amount: string;
  incurredOn: string;
}

/**
 * The typed-but-unsaved amounts, and the one Save that writes them.
 *
 * Lives at PAGE level, above the table, so what was typed survives switching
 * between Needs data and All lines: it is the same line either way, and a
 * buffer that reset on that switch would silently drop what you typed.
 *
 * Nothing is written until Save. That is the point: forty amounts should be one
 * decision, not forty chances to leave the month half done.
 */
function useAmountDrafts(period: GridPeriod | null) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, AmountDraft>>({});
  const [failures, setFailures] = useState<Record<string, string>>({});
  const periodStart = period?.periodStart ?? '';

  // One key per batch as typed. A retry of the SAME batch (its first response
  // lost) reuses it, so the server replays rather than records twice; any edit
  // makes it a different batch and drops the key.
  const idempotencyKey = useRef<string | null>(null);
  // The last COMPLETE window, and the buffer as of this render, for the reset
  // below to compare against without re-running on every keystroke.
  const activePeriod = useRef<GridPeriod | null>(period);
  const draftNow = useRef(draft);
  draftNow.current = draft;

  // A figure typed for September must never be saved into October, so a real
  // change of window clears the buffer — and SAYS so, because otherwise a screen
  // of typed amounts vanishes without a trace. A null window is not a change:
  // it is a Custom range half-picked, and costing the buffer for that would
  // punish someone for opening the date picker.
  const periodKey = period ? `${period.periodStart}_${period.periodEnd}` : null;
  useEffect(() => {
    if (period === null) return;
    const prev = activePeriod.current;
    activePeriod.current = period;
    if (prev && prev.periodStart === period.periodStart && prev.periodEnd === period.periodEnd) {
      return;
    }
    const lost = Object.values(draftNow.current).filter((v) => v.amount.trim() !== '').length;
    setDraft({});
    setFailures({});
    idempotencyKey.current = null;
    if (lost > 0) {
      toast.warning(`${lost} unsaved ${lost === 1 ? 'amount' : 'amounts'} discarded`, {
        description: 'They were entered for a different date range.',
      });
    }
  }, [periodKey]);

  const entries = Object.entries(draft).filter(([, v]) => v.amount.trim() !== '');
  const dirty = entries.length;

  const put = (lineId: string, patch: Partial<AmountDraft>) => {
    idempotencyKey.current = null;
    setDraft((d) => ({
      ...d,
      [lineId]: { amount: '', incurredOn: periodStart, ...d[lineId], ...patch },
    }));
  };

  const discard = () => {
    idempotencyKey.current = null;
    setDraft({});
    setFailures({});
  };

  /** Only dates inside the window. One outside it would be saved to a different
   *  month's invoice while this row went on reading "No amount recorded" — a
   *  success that looks exactly like a failure. Clearing the picker snaps back
   *  to the window's start rather than leaving a blank date to fail on Save. */
  const setDate = (lineId: string, incurredOn: string) => {
    if (period === null) return;
    if (incurredOn === '') {
      put(lineId, { incurredOn: period.periodStart });
      return;
    }
    if (incurredOn < period.periodStart || incurredOn >= period.periodEnd) {
      toast.error(`Pick a date inside ${describePeriod(period)}`, {
        description: 'An amount dated outside it would land on a different invoice.',
      });
      return;
    }
    put(lineId, { incurredOn });
  };

  const save = useMutation({
    mutationFn: () => {
      idempotencyKey.current ??= crypto.randomUUID();
      return variableExpenseOccurrencesApi.bulkCreate(
        entries.map(([customerExpenseId, v]) => ({
          customerExpenseId,
          incurredOn: v.incurredOn,
          amount: v.amount.trim(),
        })),
        { idempotencyKey: idempotencyKey.current },
      );
    },
    onSuccess: ({ data }) => {
      idempotencyKey.current = null;
      // Failed rows KEEP their typed value and their place. Clearing the whole
      // buffer on a partial success would silently discard figures the user
      // entered and the server never took (G15).
      const stillFailing: Record<string, string> = {};
      for (const f of data.failed) stillFailing[f.customerExpenseId] = f.error;
      setFailures(stillFailing);
      setDraft((current) => {
        const next: Record<string, AmountDraft> = {};
        for (const id of Object.keys(stillFailing)) {
          const v = current[id];
          if (v !== undefined) next[id] = v;
        }
        return next;
      });

      queryClient.invalidateQueries({ queryKey: queryKeys.variableExpenseOccurrences.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });

      if (data.failed.length === 0) {
        toast.success(
          `${data.created.length} ${data.created.length === 1 ? 'amount' : 'amounts'} saved`,
        );
      } else {
        toast.warning(`${data.created.length} saved · ${data.failed.length} couldn't be saved`, {
          description: 'The ones that failed are still on screen with the reason.',
        });
      }
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not save'),
  });

  return {
    draft,
    failures,
    dirty,
    periodStart,
    setAmount: (lineId: string, amount: string) => put(lineId, { amount }),
    setDate,
    discard,
    save,
  };
}

type AmountDrafts = ReturnType<typeof useAmountDrafts>;

/** The one control that writes. Appears only once something is typed, so the
 *  resting screen stays quiet. */
function SaveBar({ drafts }: { drafts: AmountDrafts }) {
  const { dirty, save, discard } = drafts;
  if (dirty === 0) return null;
  return (
    <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-amber-50 px-4 py-3 dark:bg-amber-950/40">
      <span className="font-medium text-amber-800 text-sm dark:text-amber-400">
        {dirty} {dirty === 1 ? 'amount' : 'amounts'} entered, not yet saved
      </span>
      <div className="flex gap-2">
        <Button disabled={save.isPending} onClick={discard} size="sm" variant="ghost">
          Discard
        </Button>
        <Button disabled={save.isPending} onClick={() => save.mutate()} size="sm">
          {save.isPending ? 'Saving…' : `Save ${dirty} ${dirty === 1 ? 'amount' : 'amounts'}`}
        </Button>
      </div>
    </div>
  );
}

/**
 * The amount box a line with nothing recorded is waiting for.
 *
 * Feeds the page's one buffer, so the row that needs a figure takes it the same
 * way whichever lines are showing, and nothing typed is lost by switching.
 */
function DraftAmountInput({
  row,
  drafts,
  canEdit,
}: {
  row: VariableExpenseGridRow;
  drafts: AmountDrafts;
  canEdit: boolean;
}) {
  return (
    <Input
      aria-label={`Amount for ${row.customerName}, ${row.itemName}`}
      className={`ml-auto w-32 text-right tabular-nums ${
        drafts.failures[row.lineId] ? 'border-destructive' : ''
      }`}
      disabled={!canEdit || drafts.save.isPending}
      inputMode="decimal"
      onChange={(e) => drafts.setAmount(row.lineId, e.target.value)}
      placeholder="0.00"
      // Text, never `type="number"`: a scroll wheel over a focused number input
      // silently edits the figure.
      type="text"
      value={drafts.draft[row.lineId]?.amount ?? ''}
    />
  );
}

/**
 * One line, and its occurrences.
 *
 * A line with several fees renders several rows; a line with none renders one
 * highlighted row saying so. Rendering the empty case as a ROW rather than
 * omitting it is the whole point — the missing ones are what the user came to
 * fix.
 *
 * The empty row is EDITABLE in place — a date and an amount, straight into the
 * page's draft buffer. Recording the missing figure is the whole reason that
 * row is drawn, so putting it behind a + cost a click on the one action the row
 * exists for. What it does NOT get is a Save of its own: it writes through the
 * page's single Save, so forty amounts stay one decision. The + is for the
 * rarer case —
 * appending a SECOND fee to a line that already has one.
 */
function GridRows({
  row,
  drafts,
  canEdit,
}: {
  row: VariableExpenseGridRow;
  drafts: AmountDrafts;
  canEdit: boolean;
}) {
  const periodStart = drafts.periodStart;
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.variableExpenseOccurrences.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  // Which occurrence is open for editing, if any. One at a time: a row that
  // is being corrected is the user's whole focus, and several open editors
  // would make it easy to save the wrong one.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Whether the "append a further fee" row is open. Closed by default, and
  // reached by the + on the last occurrence. A line that already has an amount
  // almost never gains a second one, so a standing empty row on every line was
  // a screen full of controls nobody was going to use — and an empty date and
  // amount sitting under each line read as data still missing.
  const [adding, setAdding] = useState(false);

  const remove = useMutation({
    mutationFn: (id: string) => variableExpenseOccurrencesApi.delete(id),
    onSuccess: () => {
      invalidate();
      toast.success('Amount removed');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not remove it'),
  });

  const identity = (
    <>
      <TableCell className="align-top font-medium">{row.customerName}</TableCell>
      <TableCell className="align-top">
        <div className="truncate">{row.description}</div>
        <div className="truncate text-muted-foreground text-xs">{row.itemName}</div>
      </TableCell>
    </>
  );

  /** The row the + opens. One definition for both shapes of line, so the
   *  first amount and the fourth are recorded by exactly the same control. */
  const addFields = (
    <>
      <TableCell />
      <TableCell />
      <OccurrenceFields
        amount=""
        busyLabel="Adding…"
        date={periodStart}
        onCancel={() => setAdding(false)}
        onSubmit={(input) => variableExpenseOccurrencesApi.create(row.lineId, input)}
        onSuccess={() => {
          // Close rather than clear-and-stay: the resting state after a save is
          // the quiet one, and the + is right there for the rare repeat.
          setAdding(false);
          invalidate();
        }}
        // "Add", not "Save": this appends a fee rather than replacing one. The
        // word has to say which, because getting it wrong is how a customer
        // gets billed twice.
        submitLabel="Add"
      />
    </>
  );

  if (row.occurrences.length === 0) {
    const failure = drafts.failures[row.lineId];
    return (
      <TableRow className="bg-amber-500/5">
        {identity}
        {canEdit ? (
          <>
            <TableCell>
              <DatePicker
                className="w-[9.5rem]"
                onChange={(d) => drafts.setDate(row.lineId, d)}
                placeholder="Date"
                value={drafts.draft[row.lineId]?.incurredOn ?? periodStart}
              />
            </TableCell>
            <TableCell className="text-right">
              <DraftAmountInput canEdit={canEdit} drafts={drafts} row={row} />
              {failure ? (
                <div className="mt-1 text-right text-destructive text-xs">{failure}</div>
              ) : null}
            </TableCell>
            <TableCell />
          </>
        ) : (
          <>
            <TableCell className="text-amber-700 text-sm dark:text-amber-500" colSpan={2}>
              No amount recorded
            </TableCell>
            <TableCell />
          </>
        )}
      </TableRow>
    );
  }

  return (
    <>
      {row.occurrences.map((o, i) => (
        // `group` so the row's own hover reveals its icons — see the actions
        // cell below.
        <TableRow className="group" key={o.id}>
          {/* The customer and expense are printed once per line, not once per
              occurrence: three fees for one arrangement are one thing that
              happened three times, and repeating the name reads as three
              separate arrangements. */}
          {i === 0 ? (
            identity
          ) : (
            <>
              <TableCell />
              <TableCell />
            </>
          )}
          {editingId === o.id ? (
            <OccurrenceFields
              amount={o.amount}
              busyLabel="Saving…"
              date={o.incurredOn}
              onCancel={() => setEditingId(null)}
              onSubmit={(input) => variableExpenseOccurrencesApi.patch(o.id, input)}
              onSuccess={() => {
                setEditingId(null);
                invalidate();
              }}
              submitLabel="Save"
            />
          ) : (
            <>
              <TableCell>{o.incurredOn}</TableCell>
              {/* The stored string, formatted — never parsed through Number (G15). */}
              <TableCell className="text-right tabular-nums">{formatMoney(o.amount)}</TableCell>
              <TableCell className="whitespace-nowrap">
                {canEdit ? (
                  /* Fixed slots, right-aligned as a group. The + belongs to
                     one row per line, so without a reserved slot the pencil
                     and bin of every OTHER row slide sideways and the column
                     stops reading as a column. `invisible` keeps the box and
                     drops it from the tab order and the a11y tree.
                     Revealed on hover (and on keyboard focus, which hover
                     alone would strand): at rest the screen is the figures,
                     which is what someone scanning a month is reading. Opacity
                     rather than display, so nothing shifts as it appears. */
                  <div className="flex items-center justify-end opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                    <Button
                      aria-label="Edit this amount"
                      onClick={() => setEditingId(o.id)}
                      size="icon"
                      variant="ghost"
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      aria-label="Remove this amount"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(o.id)}
                      size="icon"
                      variant="ghost"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                    {/* Only on the LAST occurrence, and only one per line: the
                        new row appears directly beneath it, so the + has to sit
                        where the row will open. */}
                    <Button
                      aria-label={`Add another amount for ${row.customerName}, ${row.itemName}`}
                      className={
                        i === row.occurrences.length - 1 && !adding ? undefined : 'invisible'
                      }
                      onClick={() => setAdding(true)}
                      size="icon"
                      variant="ghost"
                    >
                      <Plus className="size-4" />
                    </Button>
                  </div>
                ) : null}
              </TableCell>
            </>
          )}
        </TableRow>
      ))}
      {canEdit && adding ? <TableRow>{addFields}</TableRow> : null}
    </>
  );
}

/**
 * The date + amount pair, shared by recording a new amount and correcting one.
 *
 * Emits its own `<TableCell>`s — Date, Amount, then the buttons — rather than
 * one `colSpan` cell holding a flex row. That is what keeps the inputs sitting
 * directly under the Date and Amount headers, and lined up with the plain text
 * of every row that already has an amount. A colSpan cell lays its contents out
 * against the whole remaining width and drifts out of the columns it belongs
 * to, which is exactly what it looked like.
 *
 * One component rather than two because add and edit differ only in which
 * request they send: the validation, the money handling and the keyboard
 * behaviour must be identical, and a second copy is how an edit form quietly
 * stops rejecting a blank the way the add form does.
 *
 * `amount` is held and sent as a STRING throughout. The API canonicalizes it to
 * two decimals and rejects a third rather than rounding; parsing it through
 * `Number` here would reintroduce exactly the float error the column type
 * exists to avoid (G15).
 */
function OccurrenceFields({
  date: initialDate,
  amount: initialAmount,
  submitLabel,
  busyLabel,
  onSubmit,
  onSuccess,
  onCancel,
  clearOnSuccess = false,
}: {
  date: string;
  amount: string;
  submitLabel: string;
  busyLabel: string;
  onSubmit: (input: { incurredOn: string; amount: string }) => Promise<unknown>;
  onSuccess: () => void;
  onCancel?: () => void;
  clearOnSuccess?: boolean;
}) {
  const [date, setDate] = useState(initialDate);
  const [amount, setAmount] = useState(initialAmount);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => onSubmit({ incurredOn: date, amount }),
    onSuccess: () => {
      if (clearOnSuccess) setAmount('');
      setFieldError(null);
      onSuccess();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'Could not save that amount';
      // Inline AND a toast: the inline copy anchors the problem to the field,
      // the toast survives the row scrolling out of view.
      setFieldError(message);
      toast.error(message);
    },
  });

  const submit = () => {
    if (amount.trim() === '') {
      setFieldError('Enter an amount first');
      return;
    }
    save.mutate();
  };

  return (
    <>
      <TableCell>
        {/* The app's own date control, so this screen picks dates the same way
            every other one does. It stores plain ISO strings, which is what the
            API takes and what the period comparison needs. */}
        <DatePicker className="w-[9.5rem]" onChange={setDate} placeholder="Date" value={date} />
      </TableCell>
      <TableCell className="text-right">
        <Input
          aria-label="Amount"
          className="ml-auto h-9 w-28 text-right"
          inputMode="decimal"
          onChange={(e) => {
            setAmount(e.target.value);
            if (fieldError) setFieldError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape' && onCancel) onCancel();
          }}
          placeholder="0.00"
          value={amount}
        />
        {fieldError ? (
          <div className="mt-1 text-right text-destructive text-xs">{fieldError}</div>
        ) : null}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right">
        <Button disabled={save.isPending} onClick={submit} size="sm" variant="outline">
          {save.isPending ? busyLabel : submitLabel}
        </Button>
        {onCancel ? (
          <Button disabled={save.isPending} onClick={onCancel} size="sm" variant="ghost">
            Cancel
          </Button>
        ) : null}
      </TableCell>
    </>
  );
}
